import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { dynamicFormsApi } from './dynamic-forms.api';
import { dynamicFormKeys } from './dynamic-forms.queries';
import type {
  CreateDynamicFormRequest,
  UpdateDynamicFormRequest,
  SyncFieldsRequest
} from '../types';

export function useCreateDynamicFormMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('dynamicForms');

  return useMutation({
    mutationFn: (data: CreateDynamicFormRequest) =>
      dynamicFormsApi.create(data),
    meta: {
      operationType: 'create',
      fallbackSuccessMessage: t('createSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dynamicFormKeys.lists() });
    }
  });
}

export function useUpdateDynamicFormMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('dynamicForms');

  return useMutation({
    mutationFn: ({
      id,
      data
    }: {
      id: number;
      data: UpdateDynamicFormRequest;
    }) => dynamicFormsApi.update(id, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('updateSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dynamicFormKeys.lists() });
    }
  });
}

export function useSyncFieldsMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('dynamicForms');

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: SyncFieldsRequest }) =>
      dynamicFormsApi.syncFields(id, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('fieldsSynced')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dynamicFormKeys.all });
    }
  });
}

export function useActivateDynamicFormMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('dynamicForms');

  return useMutation({
    mutationFn: (id: number) => dynamicFormsApi.activate(id),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('activateSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dynamicFormKeys.all });
    }
  });
}

export function useDeactivateDynamicFormMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('dynamicForms');

  return useMutation({
    mutationFn: (id: number) => dynamicFormsApi.deactivate(id),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('deactivateSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dynamicFormKeys.all });
    }
  });
}

export function useSetDefaultDynamicFormMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('dynamicForms');

  return useMutation({
    mutationFn: (id: number) => dynamicFormsApi.setDefault(id),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('setDefaultSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dynamicFormKeys.all });
    }
  });
}

export function useUnsetDefaultDynamicFormMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('dynamicForms');

  return useMutation({
    mutationFn: (id: number) => dynamicFormsApi.unsetDefault(id),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('unsetDefaultSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dynamicFormKeys.all });
    }
  });
}

export function useDuplicateDynamicFormMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('dynamicForms');

  return useMutation({
    mutationFn: (id: number) => dynamicFormsApi.duplicate(id),
    meta: {
      operationType: 'create',
      fallbackSuccessMessage: t('duplicateSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dynamicFormKeys.lists() });
    }
  });
}

export function useDeleteDynamicFormMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('dynamicForms');

  return useMutation({
    mutationFn: (id: number) => dynamicFormsApi.delete(id),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('deleteSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dynamicFormKeys.lists() });
    }
  });
}
